import { apiClient } from './client';
import type { ApiError } from '@/types';

export interface FieldSummary {
  id: string;
  name: string;
  type: string;
  coordinate: string | null;
  can_manage: boolean;
}

export interface FieldList {
  fields: FieldSummary[];
  total: number;
}

export interface LayerPropertyInfo {
  id: string;
  name: string;
  value_type: string;
  value: string | number | null;
  unit?: string | null;
  description?: string | null;
}

export interface LayerInfo {
  id: string;
  name: string;
  description: string | null;
  type: string | null; // oil, gas, oil and gas
  use_for_ml?: boolean;
  properties: LayerPropertyInfo[];
}

export interface FieldWellSummary {
  id: string;
  name: string;
  entry_date: string | null;
  shut_down_date: string | null;
}

export interface FieldWellTypeGroup {
  type: string;
  type_id: string;
  wells: FieldWellSummary[];
}

export interface FieldLayerWellsGroup {
  layer_id: string;
  types: FieldWellTypeGroup[];
}

export interface ScenarioInfo {
  id: string;
  name: string;
  description: string | null;
  calculation_status: string;
  calculated_npv: number | null;
  to_date: string;
}

export interface FieldProductionData {
  year: number;
  oil: number | null;
  gas: number | null;
  water: number | null;
}

export interface FieldDetail {
  id: string;
  name: string;
  type: string;
  description: string | null;
  is_active: boolean;
  contour: string | null;
  coordinate: string | null;
  visibility_status_id: string;
  can_manage: boolean;
  can_manage_resources: boolean;
  layers_count: number;
  wells_count: number;
  scenarios_count: number;
  layers: LayerInfo[];
  wells: FieldLayerWellsGroup[];
  scenarios: ScenarioInfo[];
  production_data: FieldProductionData[];
  current_kin: number | null;  // Current oil recovery factor
  planned_kin: number | null;  // Planned oil recovery factor
}

export interface FieldTypeInfo {
  id: string;
  name: string;
}

export interface FieldTypeList {
  types: FieldTypeInfo[];
}

export interface FieldVisibilityOption {
  id: string;
  name: string;
  description?: string | null;
}

export interface FieldVisibilityOptionList {
  options: FieldVisibilityOption[];
}

export interface FieldCreatePayload {
  name: string;
  type_id: string;
  visibility_status_id?: string;
  coordinate?: string | null;
  description?: string | null;
}

export interface FieldUpdatePayload {
  name?: string;
  type_id?: string;
  visibility_status_id?: string;
  coordinate?: string | null;
  description?: string | null;
}

export interface FieldListParams {
  search?: string;
  /** Omit or 'all' = no visibility filter (still ACL-scoped). */
  visibility_status?: 'all' | 'public' | 'company_public' | 'private';
  skip?: number;
  /** Omit to return all matching rows (map / legacy). */
  limit?: number;
}

export const fieldsApi = {
  /**
   * Get list of fields visible to the current user (optional search / visibility / pagination).
   */
  getFieldsList: async (params: FieldListParams = {}): Promise<FieldList> => {
    try {
      const query = new URLSearchParams();
      if (params.search?.trim()) {
        query.set('search', params.search.trim());
      }
      if (params.visibility_status && params.visibility_status !== 'all') {
        query.set('visibility_status', params.visibility_status);
      }
      if (params.skip != null) {
        query.set('skip', String(params.skip));
      }
      if (params.limit != null) {
        query.set('limit', String(params.limit));
      }
      const qs = query.toString();
      return await apiClient.get(`/fields/list${qs ? `?${qs}` : ''}`);
    } catch (e) {
      const err = e as Partial<ApiError> | undefined;
      if (err?.status_code === 403) {
        return { fields: [], total: 0 };
      }
      throw e;
    }
  },

  /**
   * Get detailed information about specific field
   */
  getFieldDetail: async (fieldId: string): Promise<FieldDetail> => {
    return await apiClient.get(`/fields/${fieldId}`);
  },

  getFieldTypes: async (): Promise<FieldTypeList> => {
    return await apiClient.get('/fields/types');
  },

  getVisibilityOptions: async (): Promise<FieldVisibilityOptionList> => {
    return await apiClient.get('/fields/visibility-options');
  },

  createField: async (payload: FieldCreatePayload): Promise<FieldSummary> => {
    return await apiClient.post('/fields/', payload);
  },

  updateField: async (fieldId: string, payload: FieldUpdatePayload): Promise<FieldSummary> => {
    return await apiClient.put(`/fields/${fieldId}`, payload);
  },

  deleteField: async (fieldId: string): Promise<void> => {
    await apiClient.delete(`/fields/${fieldId}`);
  },
};
